import { create } from "zustand";
import type { TransactionSource, TransactionStatus } from "api/responses";

/**
 * Identifies each type of filter we support.
 * See Vyne.Payments.BusinessLogic.Queries.PaidReportDetailStatus.
 */
export type PaymentsActivityStatusFilterType = TransactionStatus | "RefundAll";

/**
 * The list of Transaction statuses that can be filtered by.
 *
 * Also serves as the initial filter, because we display all statuses by default.
 */
export const initialStatuses: PaymentsActivityStatusFilterType[] = ["RefundAll", "RefundPartial", "Paid"];

export const transactionStatusToText: Record<PaymentsActivityStatusFilterType, string> = {
    RefundAll: "Refund",
    RefundFull: "Refund",
    RefundPartial: "Partial Refund",
    Paid: "Paid",
};

/**
 * The list of Transaction sources that can be filtered by.
 *
 * Also serves as the initial filter, because we display all sources by default.
 */
export const initialSources: TransactionSource[] = [
    "None",
    "Dashboard",
    "Email",
    "SMS",
    "Statement",
    "DashboardLink",
    "BillPay",
    "DashboardInsurance",
];

export const transactionSourceValues: Record<TransactionSource, string> = {
    None: "Unknown",
    Dashboard: "Take a Payment",
    Email: "Email",
    SMS: "SMS",
    Statement: "Paper Statement",
    API: "Vyne Trellis",
    DashboardLink: "Dashboard Link",
    BillPay: "Bill Pay",
    DashboardInsurance: "Insurance",
};

interface PaymentsActivityStatusFilterState {
    /** Patient name filter. */
    patientName: string;

    /** The list of active filters that should be applied to the API request. */
    statuses: PaymentsActivityStatusFilterType[];

    /** The list of transaction sources that should be applied to the API request. */
    sources: TransactionSource[];

    /** Function to toggle the status of a display filter. */
    toggleStatus: (filterType: PaymentsActivityStatusFilterType, enabled: boolean) => void;

    /** Function to toggle the source of a display filter. */
    toggleSource: (updatedSource: TransactionSource, enabled: boolean) => void;

    /** Function to set patient name filter. */
    setPatientName: (patientName: string) => void;

    /** Function to reset status and source filters back to defaults. */
    resetFilters: () => void;
}

/** Hook to track the state of filters for payment activity. */
export const usePaymentsActivityStatusFilters = create<PaymentsActivityStatusFilterState>()((set) => {
    return {
        statuses: [...initialStatuses],
        sources: [...initialSources],
        patientName: "",
        toggleStatus: (filterType: PaymentsActivityStatusFilterType, enabled: boolean) =>
            set((state) => handleToggleStatus(state, filterType, enabled)),
        toggleSource: (updatedSource: TransactionSource, enabled: boolean) =>
            set((state) => handleToggleSource(state, updatedSource, enabled)),
        setPatientName: (patientName: string) => set({ patientName: patientName }),
        resetFilters: () => set({ patientName: "", statuses: [...initialStatuses], sources: [...initialSources] }),
    };
});

function handleToggleStatus(
    state: PaymentsActivityStatusFilterState,
    status: PaymentsActivityStatusFilterType,
    enabled: boolean,
) {
    let newStatuses = [...state.statuses];

    const index = state.statuses.indexOf(status);

    // if enabled and not already included
    if (enabled && index < 0) {
        newStatuses.push(status);
    } else if (!enabled && index >= 0) {
        newStatuses.splice(index, 1);
    }

    // handle all statuses being toggled off
    if (newStatuses.length === 0) {
        // set all to true
        newStatuses = [...initialStatuses];
    }

    return {
        statuses: newStatuses,
    };
}

function handleToggleSource(state: PaymentsActivityStatusFilterState, source: TransactionSource, enabled: boolean) {
    let newSources = [...state.sources];

    const index = state.sources.indexOf(source);
    if (enabled && index < 0) {
        newSources.push(source);
    } else if (!enabled && index >= 0) {
        newSources.splice(index, 1);
    }

    // handle all sources being toggled off
    if (newSources.length === 0) {
        // set all to true
        newSources = [...initialSources];
    }

    return {
        sources: newSources,
    };
}
